import {
  BillingCycle,
  Permission,
  type PreviewPlanChangeResponseDto,
  SubscriptionStatus,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription, Button, cn } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { PLAN_PRICING, TRIAL_DAYS } from '@/core/billing/plan-catalog';
import {
  readUpgradeFocusFeature,
  recommendedUpgradePlan,
} from '@/core/billing/upgrade-focus';
import { useFormatEur } from '@/core/billing/use-format-eur';
import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { Can } from '@/core/components/authz/can';
import { EnterpriseContactDialog } from '@/core/components/billing/enterprise-contact-dialog';
import { formatRenewalDate } from '@/core/components/billing/format-renewal-date';
import { PlanPicker } from '@/core/components/billing/plan-picker';
import { resolveSidebarUpgradeTeaserVariant } from '@/core/components/billing/resolve-sidebar-upgrade-teaser-variant';
import { useEntitlementLabels } from '@/core/components/billing/upgrade-surfaces';
import { PageHeader } from '@/core/components/page-header';
import {
  useChangeSubscriptionPlan,
  usePreviewPlanChange,
  useStartCheckout,
} from '@/core/hooks/use-billing';
import { useBillingReturn } from '@/core/hooks/use-billing-return';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';

function UpgradeBreadcrumb({
  domainLabel,
  billingLabel,
  plansLabel,
  isMobile,
}: {
  domainLabel: string;
  billingLabel: string;
  plansLabel: string;
  isMobile: boolean;
}) {
  if (isMobile) {
    return (
      <>
        {billingLabel}
        <span className="mx-1.5 text-muted-foreground/70">/</span>
        {plansLabel}
      </>
    );
  }
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {billingLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {plansLabel}
    </>
  );
}

export function WorkspaceBillingUpgradePage() {
  const dict = useDictionary();
  const workspaceDict = dict.workspace.billing;
  const plansPage = workspaceDict.plansPage;
  const planPickerDict = workspaceDict.planPicker;
  const formatEur = useFormatEur();
  const currentWorkspace = useCurrentWorkspace();
  const isMobile = useIsMobile();
  const usesMobileTabBar = shellUsesMobileTabBar();
  const { navigateShell } = useNavigateShell();
  const { plan, status, usage } = useEntitlements();
  const { getPlanLabel, getFeatureLabel } = useEntitlementLabels();
  const location = useLocation();
  const focusFeature = readUpgradeFocusFeature(location.search);
  const recommendedPlan = recommendedUpgradePlan(focusFeature);
  /** Trial CTAs (sticky, preselection) only exist for Plus and Teams. */
  const trialPlan =
    recommendedPlan === WorkspacePlan.TEAMS
      ? WorkspacePlan.TEAMS
      : WorkspacePlan.PLUS;
  const { can } = usePermissions();
  const canManage = can(Permission.BILLING_MANAGE);
  const startCheckout = useStartCheckout();
  const changePlan = useChangeSubscriptionPlan();
  const previewPlanChange = usePreviewPlanChange();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [planPreview, setPlanPreview] =
    useState<PreviewPlanChangeResponseDto | null>(null);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [selectedPlanOverride, setSelectedPlanOverride] =
    useState<WorkspacePlan | null>(null);
  const [billingCycleOverride, setBillingCycleOverride] =
    useState<BillingCycle | null>(null);

  useEffect(() => {
    if (!canPurchaseBillingInApp()) {
      navigateShell(shellPaths.moreWorkspaceBilling);
    }
  }, [navigateShell]);

  useBillingReturn();

  const currentWorkspacePlan = plan ?? null;
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : 'de-DE';
  const hasPaidSubscription =
    plan !== WorkspacePlan.FREE &&
    (status === SubscriptionStatus.ACTIVE ||
      status === SubscriptionStatus.TRIALING ||
      status === SubscriptionStatus.PAST_DUE);
  const hasStripeCustomer = Boolean(currentWorkspace?.stripeCustomerId);
  const shouldChangePlanInPlace =
    plan !== WorkspacePlan.FREE &&
    plan !== WorkspacePlan.ENTERPRISE &&
    (hasPaidSubscription || hasStripeCustomer);
  const currentBillingCycle =
    currentWorkspace?.billingCycle === BillingCycle.YEARLY
      ? BillingCycle.YEARLY
      : BillingCycle.MONTHLY;
  const promoteCheckoutTrial =
    plan != null &&
    resolveSidebarUpgradeTeaserVariant({ plan, status }) === 'trialStart';
  const trialDaysLabel = String(TRIAL_DAYS);

  const defaultSelectedPlan = promoteCheckoutTrial
    ? trialPlan
    : currentWorkspacePlan;
  const selectedPlan = selectedPlanOverride ?? defaultSelectedPlan;
  const billingCycle = billingCycleOverride ?? currentBillingCycle;

  const planWarnings = useMemo(() => {
    if (!selectedPlan || !usage) return [];
    const warnings: string[] = [];
    const members = usage.members;
    const rep = usage.reputationLocations;

    if (
      selectedPlan === WorkspacePlan.FREE ||
      selectedPlan === WorkspacePlan.PLUS
    ) {
      if (members.active > 1) {
        warnings.push(
          workspaceDict.conflicts.members
            .replace('{count}', String(members.active - 1))
            .replace('{target}', getPlanLabel(WorkspacePlan.PLUS)),
        );
      }
    }

    if (selectedPlan === WorkspacePlan.PLUS && rep) {
      const targetIncluded = 1;
      if (rep.connected > targetIncluded) {
        warnings.push(
          workspaceDict.conflicts.reputationAddon
            .replace('{count}', String(rep.connected - targetIncluded))
            .replace('{target}', getPlanLabel(WorkspacePlan.PLUS)),
        );
      }
    }

    if (selectedPlan === WorkspacePlan.FREE && rep) {
      if (rep.connected > 0) {
        warnings.push(
          workspaceDict.conflicts.reputationLocations
            .replace('{count}', String(rep.connected))
            .replace('{target}', getPlanLabel(WorkspacePlan.FREE)),
        );
      }
    }

    return warnings;
  }, [getPlanLabel, selectedPlan, usage, workspaceDict.conflicts]);

  const runPlanAction = async (targetPlan: WorkspacePlan) => {
    if (targetPlan === WorkspacePlan.ENTERPRISE) {
      setEnterpriseOpen(true);
      return;
    }

    setSelectedPlanOverride(targetPlan);

    if (!shouldChangePlanInPlace) {
      if (targetPlan === WorkspacePlan.FREE) return;
      startCheckout.mutate({
        plan: targetPlan,
        billingCycle,
      });
      return;
    }

    if (
      targetPlan === currentWorkspacePlan &&
      billingCycle === currentBillingCycle
    ) {
      return;
    }

    try {
      const preview = await previewPlanChange.mutateAsync({
        plan: targetPlan,
        billingCycle,
      });
      setPlanPreview(preview);
      setConfirmOpen(true);
    } catch {
      // Toast handled in hook
    }
  };

  const handleExecutePlanChange = () => {
    if (!selectedPlan) return;
    setConfirmOpen(false);
    changePlan.mutate({
      plan: selectedPlan,
      billingCycle,
    });
  };

  const confirmTitle =
    planPreview?.kind === 'cancel'
      ? workspaceDict.confirm.cancelTitle
      : planPreview?.timing === 'period_end'
        ? workspaceDict.confirm.downgradeTitle
        : workspaceDict.confirm.upgradeTitle;

  const confirmDescription =
    planPreview?.kind === 'cancel'
      ? workspaceDict.confirm.cancelDescription
      : planPreview?.timing === 'period_end'
        ? workspaceDict.confirm.downgradeDescription
        : workspaceDict.confirm.upgradeDescription;

  const breadcrumb = (
    <UpgradeBreadcrumb
      domainLabel={dict.nav.navDomainWorkspaceAdmin}
      billingLabel={dict.nav.billing}
      plansLabel={planPickerDict.plansBreadcrumb}
      isMobile={isMobile}
    />
  );

  const ctaBusy =
    startCheckout.isPending ||
    changePlan.isPending ||
    previewPlanChange.isPending;

  const stickyPrice =
    billingCycle === BillingCycle.YEARLY
      ? plansPage.stickySubYearly.replace(
          '{yearly}',
          formatEur(PLAN_PRICING[trialPlan].yearly),
        )
      : plansPage.stickySubMonthly.replace(
          '{price}',
          formatEur(PLAN_PRICING[trialPlan].monthly),
        );

  if (!canPurchaseBillingInApp()) {
    return null;
  }

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            breadcrumb={breadcrumb}
            title={plansPage.title}
            backHref={shellPaths.moreWorkspaceBilling}
          />

          <div
            className={cn(
              'flex w-full min-w-0 flex-col gap-7 pb-6',
              // Room for the sticky trial CTA (fade + button + launcher gap).
              isMobile &&
                canManage &&
                promoteCheckoutTrial &&
                (usesMobileTabBar ? 'pb-36' : 'pb-48'),
            )}
          >
            {!canManage ? (
              <Alert>
                <AlertDescription>
                  {workspaceDict.readOnlyHint}
                </AlertDescription>
              </Alert>
            ) : null}

            <PlanPicker
              selectedPlan={selectedPlan}
              onPlanChange={setSelectedPlanOverride}
              billingCycle={billingCycle}
              onBillingCycleChange={setBillingCycleOverride}
              currentPlan={currentWorkspacePlan}
              promoteTrial={promoteCheckoutTrial}
              isMobile={isMobile}
              canManage={canManage}
              ctaBusy={ctaBusy}
              onSelectPlanAction={(p) => void runPlanAction(p)}
              onContactEnterprise={() => setEnterpriseOpen(true)}
              recommendedPlan={recommendedPlan}
              focusFeatureLabel={
                focusFeature ? getFeatureLabel(focusFeature) : null
              }
            />

            {planWarnings.length > 0 ? (
              <Alert>
                <AlertDescription>
                  <p className="font-medium">{workspaceDict.conflicts.title}</p>
                  <ul className="mt-2 list-inside list-disc text-sm">
                    {planWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            ) : null}
          </div>

          {isMobile && canManage && promoteCheckoutTrial ? (
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20">
              <div className="h-16 bg-gradient-to-t from-background via-background/90 to-transparent" />
              <div
                className={cn(
                  'bg-background px-4 pt-1',
                  // Only Cap paints a bottom tab bar. The mobile browser just
                  // clears the get-started launcher; reserving tab-bar height
                  // there left the last plan card under the fade (DEV-535).
                  usesMobileTabBar
                    ? 'pb-[var(--shell-mobile-tab-bar-clearance)]'
                    : 'pb-[max(4.5rem,calc(env(safe-area-inset-bottom)+3.25rem))]',
                )}
              >
                <Can permission={Permission.BILLING_MANAGE}>
                  <Button
                    variant="primary"
                    className="pointer-events-auto h-[52px] w-full flex-col gap-0.5 rounded-[14px] text-[15px] font-semibold shadow-[0_10px_24px_rgba(42,85,229,0.3)]"
                    disabled={ctaBusy}
                    onClick={() => void runPlanAction(trialPlan)}
                  >
                    <span>
                      {plansPage.stickyTitle
                        .replace('{plan}', getPlanLabel(trialPlan))
                        .replace('{days}', trialDaysLabel)}
                    </span>
                    <span className="text-[11px] font-medium opacity-85">
                      {stickyPrice}
                    </span>
                  </Button>
                </Can>
              </div>
            </div>
          ) : null}

          <ResponsiveDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <ResponsiveDialogContent className="sm:max-w-lg">
              <ResponsiveDialogHeader>
                <ResponsiveDialogTitle>{confirmTitle}</ResponsiveDialogTitle>
              </ResponsiveDialogHeader>
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                {confirmDescription}
              </p>
              {planPreview?.effectiveAt &&
              planPreview.timing === 'period_end' ? (
                <p className="text-[13.5px] leading-relaxed">
                  {workspaceDict.confirm.effectiveAt.replace(
                    '{date}',
                    formatRenewalDate(planPreview.effectiveAt, locale),
                  )}
                </p>
              ) : null}
              {planPreview?.willIncurLocationAddon ? (
                <Alert>
                  <AlertDescription>
                    {workspaceDict.confirm.locationAddon}
                  </AlertDescription>
                </Alert>
              ) : null}
              {(planPreview?.warnings.length ?? 0) > 0 ? (
                <Alert>
                  <AlertDescription>
                    <ul className="list-inside list-disc text-sm">
                      {planPreview?.warnings.map((warning) => (
                        <li key={warning.code}>{warning.message}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : null}
              <div
                className={cn(
                  'flex gap-2 pt-2',
                  isMobile ? 'flex-col-reverse' : 'justify-end',
                )}
              >
                <Button
                  variant="outline"
                  className={cn(isMobile && 'w-full')}
                  onClick={() => setConfirmOpen(false)}
                >
                  {workspaceDict.cancel}
                </Button>
                <Button
                  variant="primary"
                  className={cn(isMobile && 'w-full')}
                  disabled={changePlan.isPending}
                  onClick={handleExecutePlanChange}
                >
                  {workspaceDict.confirm.confirm}
                </Button>
              </div>
            </ResponsiveDialogContent>
          </ResponsiveDialog>

          <EnterpriseContactDialog
            open={enterpriseOpen}
            onOpenChange={setEnterpriseOpen}
          />
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
